<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { PageHero } from '~/components';
import { AppTextInput, AppTypography } from '~/components/base';
import {
  AddToHomeScreenBanner,
  ElectionBanner,
  ExploreSection,
  VotingAreaCard,
} from '~/components/home';
import { LocationPickerModal } from '~/components/layout';
import { useLocationStore } from '~/stores/location';

definePageMeta({ layout: 'default' });

useSeoMeta({
  description:
    'Make informed voting decisions for the 2026 Victorian State Election — explore candidates, parties, campaigns and official sources.',
  ogTitle: 'VoteWise',
  ogDescription:
    'Make informed voting decisions for the 2026 Victorian State Election — explore candidates, parties, campaigns and official sources.',
});

const modalVisible = ref(false);

// Mobile-only search bar (per Figma, the search + location chip live in a
// white card under the transparent nav instead of in the topbar).
const router = useRouter();
const location = useLocationStore();
const search = ref('');

function onSearchEnter() {
  const q = search.value.trim();
  if (!q) return;
  router.push({ path: '/search', query: { q } });
  search.value = '';
}
</script>

<template>
  <div class="home-page">
    <!-- Desktop: dark countdown strip. Mobile shows the card variant below. -->
    <ElectionBanner class="hidden lg:block" />
    <!-- Figma: voting area + explore sit in a centered ~560px column, with
         the map bleeding behind — not stretched across the page. -->
    <PageHero constrained max-width="max-w-xl">
      <!-- Mobile top region (per Figma): white search card with location
           chip, then the countdown card on the sand. -->
      <div class="flex flex-col gap-4 lg:hidden">
        <div class="home-page__mobile-search flex items-center gap-2">
          <Icon
            name="solar:magnifer-linear"
            class="home-page__mobile-search-icon text-color-subtext shrink-0"
          />
          <AppTextInput
            v-model="search"
            placeholder="Search VoteWise"
            :floating-label="false"
            variant="outlined"
            size="small"
            enterkeyhint="search"
            @keyup.enter="onSearchEnter"
          />
          <button
            type="button"
            class="home-page__mobile-location flex max-w-[45%] shrink-0 items-center gap-1 rounded-full px-3 py-2"
            @click="modalVisible = true"
          >
            <Icon
              name="solar:map-point-linear"
              size="16"
              class="home-page__mobile-location-icon shrink-0"
            />
            <AppTypography type="caption" class="truncate">
              {{ location.displayLabel }}
            </AppTypography>
          </button>
        </div>
        <ElectionBanner variant="card" />
      </div>

      <VotingAreaCard @set-location="modalVisible = true" />
      <ExploreSection />
    </PageHero>
    <LocationPickerModal v-if="modalVisible" v-model:visible="modalVisible" />
    <ClientOnly>
      <AddToHomeScreenBanner />
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
.home-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  // Fill the viewport below the fixed header so the sand band reaches the
  // footer instead of trailing off into empty white on short content.
  min-height: calc(100vh - var(--header-height));

  // Mobile search card: white rounded pill row (Figma), same borderless
  // inner-input treatment as the topbar pill.
  &__mobile-search {
    height: 52px;
    border-radius: $radius-md;
    padding: 0 $spacing-md;
    background-color: $surface-color-bg;

    :deep(.app-floating-label-wrapper) {
      display: flex;
      align-items: center;
      width: 100%;
      flex: 1;
    }

    :deep(.p-inputtext) {
      width: 100%;
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0;

      &:focus,
      &:enabled:focus {
        border: none;
        box-shadow: none;
        outline: none;
      }
    }
  }

  &__mobile-search-icon {
    font-size: 1.125rem;
  }

  &__mobile-location {
    background-color: $surface-color-tag;
    border: 1px solid $surface-color-disabled;
    color: $text-color-primary;
  }

  &__mobile-location-icon {
    color: $text-color-tag;
  }
}
</style>
